import React, { useState, useEffect, useRef, memo } from 'react';
import { toLocalDateTime } from '../utils/dateUtils';

const EditableCell = memo(({ 
  value, 
  onSave, 
  onSaveComplete, 
  type = 'text', 
  placeholder = '', 
  options = [],
  multiline = false,
  className = '',
  disabled = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      // Add a small delay to ensure the input is rendered
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          
          if (type === 'datetime-local') {
            // For datetime inputs, try to show the calendar picker
            setTimeout(() => {
              if (inputRef.current) {
                // Try the modern showPicker API first
                if (inputRef.current.showPicker) {
                  try {
                    inputRef.current.showPicker();
                  } catch (e) {
                    console.log('showPicker failed, trying alternative method');
                  }
                }
              }
            }, 150); // Increased delay for better reliability
          } else if (type === 'text' || type === 'textarea') {
            inputRef.current.select();
          }
        }
      }, 50);
    }
  }, [isEditing, type]);

const handleSave = async () => {
  const currentValue = editValue ?? '';
  const originalValue = value ?? '';

  // For select dropdowns, always save when user clicks the checkmark
  // For other input types, check if there's actually a change
  if (type !== 'select' && currentValue === originalValue) {
    setIsEditing(false);
    return;
  }

  setIsSaving(true);
  try {
    const trimmedValue = typeof currentValue === 'string' ? currentValue.trim() : currentValue;
    const valueToSave = trimmedValue === '' ? null : trimmedValue;

    await onSave(valueToSave);
    setIsEditing(false);

    if (onSaveComplete) {
      onSaveComplete();
    }

  } catch (error) {
    console.error('Save failed:', error);
    setEditValue(value || '');
  } finally {
    setIsSaving(false);
  }
};

  const handleCancel = () => {
    setEditValue(value || '');
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline) { // Only save on Enter if NOT multiline
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
    // For multiline (textarea), Enter will naturally create a new line
  };

  // Handle blur for datetime inputs differently
  const handleBlur = (e) => {
    // For datetime inputs, don't auto-save on blur to allow calendar interaction
    if (type === 'datetime-local') {
      // Only save if the blur is not going to a calendar element
      const relatedTarget = e.relatedTarget;
      if (!relatedTarget || !relatedTarget.closest('.calendar-picker')) {
        // Small delay to see if user is interacting with calendar
        setTimeout(() => {
          if (document.activeElement !== inputRef.current) {
            // User has moved focus away from the input and it's not the calendar
            // Don't auto-save, let them use the buttons
          }
        }, 100);
      }
      return;
    }
    handleSave();
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      let cleanDateStr = dateStr.replace(/\.\d{3}Z?$/, '');
      cleanDateStr = cleanDateStr.replace('T', ' ');
      
      const [datePart, timePart] = cleanDateStr.split(' ');
      const [year, month, day] = datePart.split('-');
      const [hour, minute] = timePart.split(':');
      
      const hour24 = parseInt(hour);
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const ampm = hour24 >= 12 ? 'PM' : 'AM';
      
      return `${month}/${day} ${hour12}:${minute} ${ampm}`;
    } catch (error) {
      console.error('Date formatting error:', error);
      return dateStr;
    }
  };
  
  const formatDateTimeForInput = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';

      return toLocalDateTime(date);
    } catch (error) {
      console.error('Date formatting error:', error);
      return '';
    }
  };

  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  if (!isEditing) {
    const getDisplayValue = () => {
      if (type === 'select' && options.length > 0) {
        const option = options.find(opt => opt.value === value);
        return option ? option.label : value;
      }
      
      if (type === 'datetime-local') {
        return value ? formatDateTime(value) : <span className="empty-datetime">N/A</span>;
      }

      if (type === 'date') {
        if (!value) return <span className="placeholder">{placeholder}</span>;
        const [year, month, day] = value.split('-');
        return `${parseInt(month)}/${parseInt(day)}/${year}`;
      }

      return value || <span className="placeholder">{placeholder}</span>;
    };

    return (
      <div 
    className={`editable-cell ${className} ${disabled ? 'disabled' : ''}`}
    onClick={() => {
  if (disabled) return;
  
  setIsEditing(true);
  
  // For date inputs, auto-trigger the picker after setting edit mode
  if (type === 'date' || type === 'datetime-local') {
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        if (inputRef.current.showPicker) {
          try {
            inputRef.current.showPicker();
          } catch (e) {
            inputRef.current.click();
          }
        } else {
          inputRef.current.click();
        }
      }
    }, 100);
  }
}}
    title={disabled ? '' : 'Click to edit'}
    style={{
      width: '100%',
      minHeight: '100%',
      cursor: disabled ? 'default' : 'pointer'
    }}
  >
    <div 
      className="cell-content"
      style={{
        whiteSpace: 'pre-wrap',
        wordWrap: 'break-word',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        minHeight: '30px',
        maxWidth: '100%',
        width: '100%',
        display: 'block',
        lineHeight: '1.4',
        padding: '4px 0'
      }}
    >
      {getDisplayValue()}
    </div>
  </div>
    );
  }

  return (
<div className={`editable-cell editing ${className}`} style={{ position: 'relative', zIndex: 1000, overflow: 'visible' }}>
  {type === 'select' ? (
    <select
      ref={inputRef}
      value={editValue}
      onChange={(e) => {
        const newValue = e.target.value;
        setEditValue(newValue);
        // For select dropdowns, save immediately when user makes a selection
        // This ensures the first option can be selected without issues
        setTimeout(async () => {
          setIsSaving(true);
          try {
            const valueToSave = newValue === '' ? null : newValue;
            await onSave(valueToSave);
            setIsEditing(false);

            if (onSaveComplete) {
              onSaveComplete();
            }
          } catch (error) {
            console.error('Save failed:', error);
            setEditValue(value || '');
          } finally {
            setIsSaving(false);
          }
        }, 0);
      }}
      onKeyDown={handleKeyDown}
      disabled={isSaving}
    >
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  ) : multiline ? (
    <textarea
      ref={inputRef}
      value={editValue}
      onChange={(e) => setEditValue(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      disabled={isSaving}
      rows={4}
      style={{
        minHeight: '80px',
        maxHeight: '150px',
        resize: 'vertical',
        width: '100%',
        padding: '8px',
        border: '2px solid #007bff',
        borderRadius: '6px',
        background: 'var(--dark-surface)',
        color: 'var(--text-primary)',
        fontSize: '13px',
        lineHeight: '1.4',
        fontFamily: 'inherit'
      }}
    />
  ) : type === 'date' ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <input
        ref={inputRef}
        type="date"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={isSaving}
        style={{
          background: 'var(--dark-surface)',
          color: 'var(--text-primary)',
          border: '2px solid #007bff',
          borderRadius: '6px',
          padding: '6px 8px',
          fontSize: '13px',
          minWidth: '150px'
        }}
      />
      <button
        type="button"
        onClick={() => {
          if (inputRef.current) {
            inputRef.current.focus();
            if (inputRef.current.showPicker) {
              try {
                inputRef.current.showPicker();
              } catch (e) {
                inputRef.current.click();
              }
            } else {
              inputRef.current.click();
            }
          }
        }}
        title="Open Calendar"
        style={{
          padding: '4px 8px',
          background: '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '12px'
        }}
      >
        📅
      </button>
    </div>
  ) : type === 'datetime-local' ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <input
        ref={inputRef}
        type="datetime-local"
        value={formatDateTimeForInput(editValue)}
        onChange={(e) => {
          console.log('DateTime input changed:', e.target.value);
          setEditValue(e.target.value);
        }}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={isSaving}
        step="60"
        style={{
          position: 'relative',
          zIndex: 1001,
          minWidth: '200px',
          background: 'var(--dark-surface)',
          color: 'var(--text-primary)',
          border: '2px solid #007bff',
          borderRadius: '6px',
          padding: '6px 8px',
          fontSize: '13px'
        }}
      />
      <button
        type="button"
        onClick={() => {
          if (inputRef.current) {
            inputRef.current.focus();
            if (inputRef.current.showPicker) {
              try {
                inputRef.current.showPicker();
              } catch (e) {
                inputRef.current.click();
              }
            } else {
              inputRef.current.click();
            }
          }
        }}
        title="Open Calendar"
        style={{
          padding: '4px 8px',
          background: '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '12px'
        }}
      >
        📅
      </button>
    </div>
  ) : (
    <input
      ref={inputRef}
      type={type}
      value={editValue}
      onChange={(e) => setEditValue(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      disabled={isSaving}
    />
  )}
  
  {isSaving && (
    <div className="saving-indicator">
      <i className="fas fa-spinner fa-spin"></i>
    </div>
  )}
  
  <div className="edit-controls">
    <button 
      className="save-btn"
      onClick={handleSave}
      disabled={isSaving}
      title="Save (Enter)"
    >
      <i className="fas fa-check"></i>
    </button>
    <button 
      className="cancel-btn"
      onClick={handleCancel}
      disabled={isSaving}
      title="Cancel (Esc)"
    >
      <i className="fas fa-times"></i>
    </button>
  </div>
</div>
  );
});

export default EditableCell;